import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { FaShare } from 'react-icons/fa';
import { getIdea, fetchCurrentUser, getIdeaComponents, getIdeaBreadcrumb } from '../api/ideas';
import ComponentPickerModal from '../components/ComponentPicker/ComponentPickerModal';
import ShareModal from '../components/ShareModal';
import ShareLayoutModal from '../components/ShareLayoutModal';
import AccessibilityModal from '../components/AccessibilityModal';
import { updateFavicon, resetFavicon } from '../utils/favicon';
import { useIdeaDashboardState } from '../hooks/useIdeaDashboardState';
import { useIdeaDashboardHandlers } from '../hooks/useIdeaDashboardHandlers';
import { ThemeContext } from '../context/ThemeContext';
import {
  MobileChatComponent,
  EditModal,
  DeleteConfirmationModal,
  DesktopToolbar,
  MobileHeader,
  RightSidebar,
  ComponentsGrid
} from '../components/IdeaDashboard';
import { DesktopTitleHeader, DesktopDescription } from '../components/IdeaDashboard/DesktopHeader';

function IdeaDashboard({ token, refreshSidebar, setShowLoginModal }) {
  const { id } = useParams();
  const state = useIdeaDashboardState();
  const { setIdeaTheme, useIdeaTheme } = useContext(ThemeContext);
  
  // Helper function to check if current user can edit the idea
  const canEditIdea = state.currentUser && state.idea && (
    state.idea.owner_id === state.currentUser.id || 
    state.idea.collaborators?.some(collaborator => collaborator.id === state.currentUser.id)
  );
  
  const handlers = useIdeaDashboardHandlers({ id, token, setShowLoginModal, state, refreshSidebar, canEditIdea });

  const breakpoints = {
    1: 0,
    2: 1300,
    3: 1600,
    4: 1900,
    5: 2200,
    6: 2500,
  };

  // Update effective scale based on window width
  useEffect(() => {
    const updateEffectiveScale = () => {
      const width = window.innerWidth;
      let maxSupportedScale = 1;
      for (let s = 6; s >= 1; s--) {
        if (width >= breakpoints[s]) {
          maxSupportedScale = s;
          break;
        }
      }
      state.setEffectiveScale(Math.min(state.scale, maxSupportedScale));
    };

    updateEffectiveScale();
    window.addEventListener('resize', updateEffectiveScale);
    return () => window.removeEventListener('resize', updateEffectiveScale);
  }, [state.scale, state.setEffectiveScale]);

  // Handle click outside for options menu
  useEffect(() => {
    function handleClickOutside(event) {
      const isClickOutsideMenuButton = 
        (state.optionsMenuRef.current && !state.optionsMenuRef.current.contains(event.target)) &&
        (state.menuButtonRef.current && !state.menuButtonRef.current.contains(event.target));
      const isClickOutsidePortalMenu = !event.target.closest('[data-dropdown-menu]');
      
      if (isClickOutsideMenuButton && isClickOutsidePortalMenu) {
        state.setShowOptionsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter components based on search query
  useEffect(() => {
    if (!state.searchQuery.trim()) {
      state.setFilteredComponents(state.components);
    } else {
      const query = state.searchQuery.toLowerCase();
      const filtered = state.components.filter(component => {
        const titleMatch = component.title?.toLowerCase().includes(query);
        const typeMatch = component.type?.toLowerCase().includes(query);
        
        let dataMatch = false;
        if (component.data && typeof component.data === 'object') {
          const dataString = JSON.stringify(component.data).toLowerCase();
          dataMatch = dataString.includes(query);
        }
        
        return titleMatch || typeMatch || dataMatch;
      });
      state.setFilteredComponents(filtered);
    }
  }, [state.searchQuery, state.components]);

  // Update favicon when icon changes
  useEffect(() => {
    if (state.icon && state.icon !== '😊') {
      updateFavicon(state.icon);
    } else if (!state.icon) {
      resetFavicon();
    }
    return () => {
      resetFavicon();
    };
  }, [state.icon]);

  // Update document title when idea title changes
  useEffect(() => {
    if (state.title) {
      document.title = `${state.title} - Ideas`;
    } else {
      document.title = 'Ideas';
    }
    return () => {
      document.title = 'Ideas';
    };
  }, [state.title]);

  // Main data fetching effect
  useEffect(() => {
    state.setError('');
    if (id) {
      const fetchData = async () => {
        try {
          const [ideaData, componentsData, breadcrumbData] = await Promise.all([
            getIdea(id, token),
            getIdeaComponents(id, token),
            getIdeaBreadcrumb(id, token),
          ]);
          
          let userData = null;
          if (token) {
            try {
              userData = await fetchCurrentUser(token);
            } catch (userErr) {
              console.warn('Could not fetch user data:', userErr);
            }
          }
          
          state.setIdea(ideaData);
          state.setComponents(componentsData);
          state.setCurrentUser(userData);
          state.setTitle(ideaData.title);
          state.setDescription(ideaData.description || '');
          state.setIcon(ideaData.icon || '😊');
          state.setIsPinned(ideaData.is_pinned || false);
          state.setTags(ideaData.tags || []);
          state.setBreadcrumb(breadcrumbData.breadcrumb || []);
          state.setError('');
          
          // Set the idea's custom theme if it exists
          if (ideaData.custom_theme) {
            setIdeaTheme(ideaData.custom_theme);
          } else {
            setIdeaTheme(null);
          }
          
          state.setAnimateComponents(false);
          setTimeout(() => state.setAnimateComponents(true), 50);
        } catch (err) {
          console.error('Fetch error:', err);
          if (err.message === 'Session expired, please log in again' || err.response?.status === 401) {
            if (token) {
              setShowLoginModal(true);
            } else {
              state.setError('This idea is private. Please log in to access it.');
            }
          } else if (err.response?.status === 403) {
            state.setError('You do not have permission to view this idea.');
          } else if (err.response?.status === 404) {
            state.setError('Idea not found.');
          } else {
            state.setError('Failed to load idea: ' + (err.response?.data?.detail || err.message));
          }
        }
      };
      fetchData();
    }
    
    // Cleanup: reset idea theme when leaving
    return () => {
      setIdeaTheme(null);
    };
  }, [id, token, setShowLoginModal]);

  // Mobile chat behavior
  useEffect(() => {
    const handleScroll = () => {
      if (state.mobileChatOpen && !state.mobileChatExpanded) {
        state.setMobileChatOpen(false);
      }
    };

    if (state.mobileChatOpen) {
      const contentElement = state.contentRef.current;
      if (contentElement) {
        contentElement.addEventListener('scroll', handleScroll);
        return () => contentElement.removeEventListener('scroll', handleScroll);
      }
    }
  }, [state.mobileChatOpen, state.mobileChatExpanded]);



  // Handle mobile keyboard visibility
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const fullHeight = window.screen.height;
        const keyboardHeight = fullHeight - currentHeight;
        
        if (keyboardHeight > 150) {
          const chatInput = document.querySelector('.mobile-chat-input');
          if (chatInput && document.activeElement === chatInput) {
            setTimeout(() => {
              chatInput.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
            }, 100);
          }
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  if (!state.idea) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[var(--primary)] mx-auto mb-4"></div>
          <p className="text-[var(--text-secondary)]">Loading idea...</p>
        </div>
      </div>
    );
  }

  const columnCounts = {
    1: 'columns-1',
    2: 'columns-2', 
    3: 'columns-3',
    4: 'columns-3 md:columns-4',
    5: 'columns-3 md:columns-4 xl:columns-5',
    6: 'columns-3 md:columns-4 xl:columns-6',
  };

  return (
    <div className={`flex flex-col h-full bg-[var(--bg-primary)] overflow-hidden p-0 ${state.isResizing ? 'no-select' : ''}`}>
      {/* Mobile Chat Component */}
      <MobileChatComponent
        ref={state.chatRef}
        id={id}
        {...state}
        {...handlers}
        canEditIdea={canEditIdea}
      />

      {/* Mobile Header */}
      <MobileHeader
        {...state}
        {...handlers}
        canEditIdea={canEditIdea}
      />

      {/* Main Layout - Desktop */}
      <div className="flex flex-1 overflow-hidden transition-all duration-300 ease-in-out gap-4">
        {/* Left Column - Content and Components */}
        <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out">
          {/* Desktop Title Header (sticky) */}
          <DesktopTitleHeader
            {...state}
            {...handlers}
            canEditIdea={canEditIdea}
          />

          {/* Scrollable Content Area */}
          <div ref={state.contentRef} className={`flex-1 overflow-y-auto px-2 sm:px-4 pb-20 lg:pb-0 transition-all duration-300 scrollbar-thin scrollbar-thumb-[var(--primary)] scrollbar-track-transparent ${state.showRightSidebar ? 'lg:pr-0' : 'lg:pr-3 lg:-mr-3'}`}>
            {/* Desktop Description (scrollable) */}
            <DesktopDescription
              {...state}
              {...handlers}
              canEditIdea={canEditIdea}
            />

            {/* Desktop Toolbar (becomes sticky after description scrolls) */}
            <DesktopToolbar
              {...state}
              {...handlers}
              canEditIdea={canEditIdea}
            />

            {/* Components Grid */}
            <ComponentsGrid
              {...state}
              {...handlers}
              canEditIdea={canEditIdea}
              columnCounts={columnCounts}
              id={id}
            />
          </div>
        </div>

        {/* Right Sidebar */}
        <RightSidebar
          {...state}
          {...handlers}
          id={id}
          token={token}
        />
      </div>

      {/* Edit Modal */}
      <EditModal
        {...state}
        {...handlers}
        idea={state.idea}
        token={token}
        refreshSidebar={refreshSidebar}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        {...state}
        {...handlers}
        token={token}
        refreshSidebar={refreshSidebar}
      />

      {/* Share Modal */}
      {state.showShareModal && (
        <ShareModal
          idea={state.idea}
          onClose={() => state.setShowShareModal(false)}
          onUpdateIdea={handlers.handleUpdateIdeaFromShare}
          token={token}
        />
      )}

      {/* Share Layout Modal */}
      {state.showShareLayoutModal && (
        <ShareLayoutModal
          idea={state.idea}
          components={state.components}
          onClose={() => state.setShowShareLayoutModal(false)}
        />
      )}

      {/* Accessibility Modal */}
      {state.showAccessibilityModal && (
        <AccessibilityModal
          idea={state.idea}
          components={state.components}
          onClose={() => state.setShowAccessibilityModal(false)}
          onUpdateComponent={handlers.handleUpdateComponent}
        />
      )}

      {/* Component Picker */}
      {state.showComponentPicker && canEditIdea && (
        <ComponentPickerModal 
          onClose={() => state.setShowComponentPicker(false)} 
          onSelect={handlers.handleAddComponent} 
        />
      )}

      {/* Dropdown Menu Portal */}
      {handlers.DropdownMenu && <handlers.DropdownMenu />}

      {/* Resize overlay */}
      {state.isResizing && (
        <div className="fixed inset-0 z-50 cursor-col-resize bg-black/5">
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-[var(--bg-secondary)] text-[var(--text-primary)] px-3 py-1 rounded-lg shadow-lg text-sm">
            Width: {state.rightColumnWidth}px
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {state.showToast && (
        <div className="fixed top-4 right-4 z-50 bg-[var(--bg-secondary)] text-[var(--text-primary)] px-4 py-3 rounded-lg shadow-lg border border-[var(--border-color)] animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-2">
            <FaShare className="text-[var(--primary)]" />
            <span className="text-sm">{state.toastMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default IdeaDashboard;